import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';
import type { CredentialKind, TotpAlgorithm } from '@/lib/api/quality-types.js';

/**
 * Client for the workspace-scoped credential store (`cti-identity-service`, reached via the gateway
 * at `/v1/connectors/workspaces/:workspaceId/credentials`). Phase 2.5 (2.5l surfaces 5–8): these
 * replace the per-project quality credential routes (`credentialsQueryOptions(projectId)`). A
 * credential is a workspace resource, available to every workspace service (§6), so the whole surface
 * is keyed by `workspaceId`. The wire is snake_case; this maps it to camelCase for the SPA.
 *
 * Secrets never cross this boundary on a read — the list returns `{ ref, metadata }` only. Identity
 * stores the `secret` opaquely; for an app-login the secret is a JSON bag
 * (`{ username, secret, totpSeed? … }`, quality's `StoredSecret` shape) serialized here on create so
 * the server can compute an OTP preview from the seed without the seed ever leaving identity.
 */

const CREDENTIALS_BASE = '/v1/connectors/workspaces';

const KNOWN_KINDS: readonly CredentialKind[] = [
  'password',
  'oauth',
  'jwt',
  'salesforce_jwt',
  'http_headers',
  'custom',
];

const CREDENTIAL_KIND_LABELS: Record<CredentialKind, string> = {
  password: 'Password',
  oauth: 'OAuth',
  jwt: 'JWT',
  salesforce_jwt: 'Salesforce JWT',
  http_headers: 'HTTP headers',
  custom: 'Custom',
};

/** Return the user-facing label for a credential kind. */
export function credentialKindLabel(kind: CredentialKind): string {
  return CREDENTIAL_KIND_LABELS[kind] ?? kind;
}

/** Public Salesforce JWT configuration stored outside the protected secret bag. */
export interface SalesforceJwtMetadata {
  consumerKey: string;
  loginUrl: string;
  instanceUrl: string;
}

/** The non-secret metadata stored beside a credential — display fields the list view reads back. */
export interface WorkspaceCredentialMetadata {
  label: string;
  username: string;
  additionalOrigins?: string[];
  kind: CredentialKind;
  salesforce?: SalesforceJwtMetadata;
}

/** A workspace credential as the SPA consumes it (camelCase, normalized from `metadata`). No secret. */
export interface WorkspaceCredential {
  ref: string;
  label: string;
  username: string;
  additionalOrigins?: string[];
  kind: CredentialKind;
  salesforce?: SalesforceJwtMetadata;
  createdAt: string;
  updatedAt: string;
}

/** Safe presence flags for protected fields. No credential value is returned. */
export interface WorkspaceCredentialStatus {
  secretConfigured: boolean;
  totpConfigured: boolean;
}

/** The snake_case shape each list entry returns. `metadata` is whatever was stored on create. */
interface WorkspaceCredentialWire {
  ref: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** Write-only TOTP (RFC 6238) enrolment fields, serialized into the opaque secret bag. */
export interface WorkspaceCredentialTotpFields {
  totpSeed?: string;
  totpDigits?: number;
  totpPeriod?: number;
  totpAlgorithm?: TotpAlgorithm;
}

/**
 * The form input for adding a credential. `secret` and the TOTP fields are write-only: they are
 * serialized into the opaque secret bag and never read back. The rest becomes the credential's
 * metadata for display.
 */
export interface CreateWorkspaceCredentialInput extends WorkspaceCredentialTotpFields {
  username: string;
  label: string;
  additionalOrigins?: string[];
  kind: CredentialKind;
  secret: string;
  salesforce?: SalesforceJwtMetadata;
}

/**
 * The form input for editing a credential in place (the ref is unchanged). The display fields become
 * the credential's new metadata; `secret` and the TOTP fields are write-only and OPTIONAL on an edit —
 * omit them to keep the stored secret/seed, provide `secret` to rotate. Never read back.
 */
export interface UpdateWorkspaceCredentialInput extends WorkspaceCredentialTotpFields {
  username: string;
  label: string;
  additionalOrigins?: string[];
  kind: CredentialKind;
  secret?: string;
  salesforce?: SalesforceJwtMetadata;
}

/** A live TOTP code computed server-side for enrolment confirmation. The seed stays in identity. */
export interface WorkspaceCredentialOtpPreview {
  code: string;
  period: number;
  /** Epoch ms when the current code window ends — the UI ticks a local countdown to it. */
  expiresAt: number;
}

/** Error carrying the HTTP status so callers can treat a 404 (no seed enrolled) specially. */
export class WorkspaceCredentialApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'WorkspaceCredentialApiError';
  }
}

function normalizeKind(kind: unknown): CredentialKind {
  return typeof kind === 'string' &&
    (KNOWN_KINDS as readonly string[]).includes(kind)
    ? (kind as CredentialKind)
    : 'custom';
}

function credentialFromWire(
  wire: WorkspaceCredentialWire,
): WorkspaceCredential {
  const meta = wire.metadata ?? {};
  const label =
    typeof meta.label === 'string' && meta.label.length > 0
      ? meta.label
      : wire.ref;
  const username = typeof meta.username === 'string' ? meta.username : '';
  const salesforce = normalizeSalesforceMetadata(meta.salesforce);
  return {
    ref: wire.ref,
    label,
    username,
    ...(Array.isArray(meta.additionalOrigins)
      ? {
          additionalOrigins: meta.additionalOrigins.filter(
            (value): value is string => typeof value === 'string',
          ),
        }
      : {}),
    kind: normalizeKind(meta.kind),
    ...(salesforce ? { salesforce } : {}),
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

/**
 * The secret of an `http_headers` credential: the headers an outgoing request carries, by name. It is
 * stored as the whole secret, not wrapped in the login bag below.
 */
export function httpHeadersSecret(headers: Record<string, string>): string {
  return JSON.stringify({ headers });
}

/**
 * Serialize the write-only fields into the opaque secret bag quality's resolver expects
 * (`StoredSecret`). The TOTP params are only included when a seed is present — a plain secret bag
 * carries no seed and simply won't support an OTP preview.
 */
function serializeSecretBag(
  input: WorkspaceCredentialTotpFields & {
    username: string;
    secret: string;
    kind: CredentialKind;
  },
): string {
  if (input.kind === 'http_headers') return input.secret;
  const username =
    input.kind === 'salesforce_jwt' ? input.username.trim() : input.username;
  const bag: Record<string, unknown> = {
    username,
    secret: input.secret,
  };
  if (
    input.kind !== 'salesforce_jwt' &&
    input.totpSeed &&
    input.totpSeed.length > 0
  ) {
    bag.totpSeed = input.totpSeed;
    if (input.totpDigits !== undefined) bag.totpDigits = input.totpDigits;
    if (input.totpPeriod !== undefined) bag.totpPeriod = input.totpPeriod;
    if (input.totpAlgorithm !== undefined)
      bag.totpAlgorithm = input.totpAlgorithm;
  }
  return JSON.stringify(bag);
}

function buildMetadata(input: {
  label: string;
  username: string;
  kind: CredentialKind;
  additionalOrigins?: string[];
  salesforce?: SalesforceJwtMetadata;
}): WorkspaceCredentialMetadata {
  const username =
    input.kind === 'salesforce_jwt' ? input.username.trim() : input.username;
  const metadata: WorkspaceCredentialMetadata = {
    label: input.label,
    username,
    kind: input.kind,
  };
  if (input.additionalOrigins !== undefined)
    metadata.additionalOrigins = input.additionalOrigins;
  if (input.kind === 'salesforce_jwt' && input.salesforce) {
    metadata.salesforce = {
      consumerKey: input.salesforce.consumerKey.trim(),
      loginUrl: new URL(input.salesforce.loginUrl).origin,
      instanceUrl: new URL(input.salesforce.instanceUrl).origin,
    };
  }
  return metadata;
}

function normalizeSalesforceMetadata(
  value: unknown,
): SalesforceJwtMetadata | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  if (
    typeof record.consumerKey !== 'string' ||
    typeof record.loginUrl !== 'string' ||
    typeof record.instanceUrl !== 'string'
  ) {
    return undefined;
  }
  return {
    consumerKey: record.consumerKey,
    loginUrl: record.loginUrl,
    instanceUrl: record.instanceUrl,
  };
}

/** A workspace-scoped credential ref. Falls back where `crypto.randomUUID` is unavailable. */
function newCredentialRef(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `cred-${Math.random().toString(36).slice(2, 10)}`;
}

/** Content type only — `apiFetch` attaches the bearer from the renewal core. */
// eslint-disable-next-line @typescript-eslint/require-await
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json' };
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
  } catch {
    /* not JSON */
  }
  return text.trim();
}

/**
 * List a workspace's credentials. The response is wrapped — `{ credentials: [...] }` — so this reads
 * `body.credentials`, never a bare array. Metadata only; no secret.
 */
export async function listWorkspaceCredentials(
  workspaceId: string,
): Promise<WorkspaceCredential[]> {
  const res = await apiFetch(
    `${CREDENTIALS_BASE}/${encodeURIComponent(workspaceId)}/credentials`,
    { headers: await headers() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load credentials'));
  const body = (await res.json()) as {
    credentials?: WorkspaceCredentialWire[];
  };
  return (body.credentials ?? []).map(credentialFromWire);
}

/**
 * Read whether protected values are already stored for one credential. The endpoint returns only
 * booleans so the edit dialog can distinguish an intentionally blank replacement form from a
 * missing value without exposing the password/token or TOTP seed.
 */
export async function getWorkspaceCredentialStatus(
  workspaceId: string,
  ref: string,
): Promise<WorkspaceCredentialStatus> {
  const res = await apiFetch(
    `${CREDENTIALS_BASE}/${encodeURIComponent(workspaceId)}/credentials/${encodeURIComponent(
      ref,
    )}/status`,
    { headers: await headers() },
  );
  if (!res.ok) {
    throw new WorkspaceCredentialApiError(
      res.status,
      await errorMessage(res, 'Failed to load credential status'),
    );
  }
  const body = (await res.json()) as Record<string, unknown>;
  if (
    typeof body?.secret_configured !== 'boolean' ||
    typeof body?.totp_configured !== 'boolean'
  ) {
    throw new Error('Invalid credential status response');
  }
  return {
    secretConfigured: body.secret_configured,
    totpConfigured: body.totp_configured,
  };
}

/**
 * Add a credential. Generates the ref, serializes the write-only fields into the opaque secret bag,
 * and stores the display fields as metadata. Returns the created credential (its ref drives the
 * post-enrolment OTP confirmation).
 */
export async function createWorkspaceCredential(
  workspaceId: string,
  input: CreateWorkspaceCredentialInput,
): Promise<WorkspaceCredential> {
  const ref = newCredentialRef();
  const metadata = buildMetadata(input);
  const res = await apiFetch(
    `${CREDENTIALS_BASE}/${encodeURIComponent(workspaceId)}/credentials`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        ref,
        secret: serializeSecretBag(input),
        metadata,
      }),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to add credential'));
  const now = new Date().toISOString();
  return {
    ref,
    label: metadata.label,
    username: metadata.username,
    ...(metadata.additionalOrigins !== undefined
      ? { additionalOrigins: metadata.additionalOrigins }
      : {}),
    kind: metadata.kind,
    ...(metadata.salesforce ? { salesforce: metadata.salesforce } : {}),
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Edit a credential in place (`PATCH .../credentials/:ref`). The ref is unchanged. `metadata` is always
 * sent (the edited display fields); `secret` is included ONLY when a new one is provided (rotation),
 * serialized into the opaque bag — omitting it keeps the stored secret/seed. The service answers
 * `{ ref, metadata, stored: true }` (404 if the ref is missing). Returns the updated credential.
 */
export async function updateWorkspaceCredential(
  workspaceId: string,
  ref: string,
  input: UpdateWorkspaceCredentialInput,
): Promise<WorkspaceCredential> {
  const metadata = buildMetadata(input);
  const body: Record<string, unknown> = { metadata };
  const secret = input.secret;
  if (secret && secret.length > 0) {
    body.secret = serializeSecretBag({ ...input, secret });
    body.preserve_totp =
      input.kind !== 'salesforce_jwt' && input.kind !== 'http_headers';
  } else if (
    input.kind !== 'salesforce_jwt' &&
    input.kind !== 'http_headers' &&
    input.totpSeed &&
    input.totpSeed.length > 0
  ) {
    body.totp_seed = input.totpSeed;
    body.totp_digits = input.totpDigits ?? 6;
    body.totp_period = input.totpPeriod ?? 30;
    body.totp_algorithm = input.totpAlgorithm ?? 'SHA1';
  }
  const res = await apiFetch(
    `${CREDENTIALS_BASE}/${encodeURIComponent(workspaceId)}/credentials/${encodeURIComponent(ref)}`,
    { method: 'PATCH', headers: await headers(), body: JSON.stringify(body) },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to update credential'));
  const now = new Date().toISOString();
  return {
    ref,
    label: metadata.label,
    username: metadata.username,
    ...(metadata.additionalOrigins !== undefined
      ? { additionalOrigins: metadata.additionalOrigins }
      : {}),
    kind: metadata.kind,
    ...(metadata.salesforce ? { salesforce: metadata.salesforce } : {}),
    createdAt: now,
    updatedAt: now,
  };
}

/** Delete a workspace credential by ref. The service answers 204 (404 if no such ref). */
export async function deleteWorkspaceCredential(
  workspaceId: string,
  ref: string,
): Promise<void> {
  const res = await apiFetch(
    `${CREDENTIALS_BASE}/${encodeURIComponent(workspaceId)}/credentials/${encodeURIComponent(ref)}`,
    { method: 'DELETE', headers: await headers() },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(await errorMessage(res, 'Failed to delete credential'));
  }
}

/**
 * Live TOTP code for a credential (enrolment confirmation). Computed server-side; the seed never
 * leaves identity. A 404/422 (no seed enrolled) surfaces as a {@link WorkspaceCredentialApiError} so
 * the caller can render nothing for a seed-less credential.
 */
export async function getWorkspaceCredentialOtpPreview(
  workspaceId: string,
  ref: string,
): Promise<WorkspaceCredentialOtpPreview> {
  const res = await apiFetch(
    `${CREDENTIALS_BASE}/${encodeURIComponent(workspaceId)}/credentials/${encodeURIComponent(
      ref,
    )}/otp-preview`,
    { method: 'POST', headers: await headers() },
  );
  if (!res.ok) {
    throw new WorkspaceCredentialApiError(
      res.status,
      await errorMessage(res, 'Failed to load TOTP code'),
    );
  }
  const body = (await res.json()) as {
    code: string;
    period: number;
    expires_at: number;
  };
  return { code: body.code, period: body.period, expiresAt: body.expires_at };
}

/** Query-key factory so mutations and the enrolment flow can invalidate precisely. */
export const workspaceCredentialKeys = {
  list: (workspaceId: string) =>
    ['workspace-credentials', workspaceId] as const,
  otpPreview: (workspaceId: string, ref: string) =>
    ['workspace-credential-otp-preview', workspaceId, ref] as const,
  status: (workspaceId: string, ref: string) =>
    ['workspace-credential-status', workspaceId, ref] as const,
};

/** TanStack Query factory for a workspace's credential list. */
export function workspaceCredentialsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: workspaceCredentialKeys.list(workspaceId),
    queryFn: () => listWorkspaceCredentials(workspaceId),
    staleTime: 30_000,
  });
}

/** Query factory for the edit dialog's protected-field presence summary. */
export function workspaceCredentialStatusQueryOptions(
  workspaceId: string,
  ref: string,
) {
  return queryOptions({
    queryKey: workspaceCredentialKeys.status(workspaceId, ref),
    queryFn: () => getWorkspaceCredentialStatus(workspaceId, ref),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * TanStack Query factory for a credential's live TOTP code. `enabled` gates it to credentials the
 * caller believes carry a seed; retry is off so a 404 (no seed) stays a stable "no TOTP" signal.
 * The refetch fires just after the code's window ends, so an expired code is never left on screen;
 * a failed refresh stops it until the caller's Retry succeeds.
 */
export function workspaceCredentialOtpPreviewQueryOptions(
  workspaceId: string,
  ref: string,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: workspaceCredentialKeys.otpPreview(workspaceId, ref),
    queryFn: () => getWorkspaceCredentialOtpPreview(workspaceId, ref),
    enabled,
    retry: false,
    refetchInterval: (query) =>
      query.state.status === 'success' && query.state.data
        ? Math.max(1000, query.state.data.expiresAt - Date.now() + 500)
        : false,
  });
}

export function useCreateWorkspaceCredential(
  workspaceId: string,
): UseMutationResult<
  WorkspaceCredential,
  Error,
  CreateWorkspaceCredentialInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceCredentialInput) =>
      createWorkspaceCredential(workspaceId, input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceCredentialKeys.list(workspaceId),
      });
    },
  });
}

export function useUpdateWorkspaceCredential(
  workspaceId: string,
): UseMutationResult<
  WorkspaceCredential,
  Error,
  { ref: string; input: UpdateWorkspaceCredentialInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      ref: string;
      input: UpdateWorkspaceCredentialInput;
    }) => updateWorkspaceCredential(workspaceId, args.ref, args.input),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({
        queryKey: workspaceCredentialKeys.list(workspaceId),
      });
      void qc.invalidateQueries({
        queryKey: workspaceCredentialKeys.otpPreview(workspaceId, args.ref),
      });
      void qc.invalidateQueries({
        queryKey: workspaceCredentialKeys.status(workspaceId, args.ref),
      });
    },
  });
}

export function useDeleteWorkspaceCredential(
  workspaceId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => deleteWorkspaceCredential(workspaceId, ref),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: workspaceCredentialKeys.list(workspaceId),
      });
    },
  });
}
